import styles from './CheckListOverlayController.module.scss';

import { Add } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckListPicker } from '@work4all/components/lib/components/entity-picker/check-list-picker/CheckListPicker';

import { useNavigate } from '@work4all/data';

import { CheckList } from '@work4all/models/lib/Classes/CheckList.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { MaskOverlayHeader } from '../../../components';
import {
  MaskContextProvider,
  useMaskConfig,
} from '../../../hooks/mask-context';
import { OverlayController } from '../../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../../overlay-controller/use-mask-overlay';
import {
  CreateChecklistFromTemplateProps,
  useCheckListFormUpdate,
  useCheckListMaskOverlayNewEntityData,
  useCheckListMaskOverlayRequest,
  useCheckListSubmitHandler,
  useCreateChecklistFromTemplate,
} from '../hooks';

import { CheckListOverlayControllerContent } from './CheckListOverlayControllerContent';
import { CheckListOverlayControllerTabs } from './CheckListOverlayControllerTabs';
import { ControllerTabs } from './enums';
import {
  CheckListMaskFormValue,
  CheckListOverlayControllerProps,
} from './types';

export const CheckListOverlayController = (
  props: CheckListOverlayControllerProps
) => {
  const mask = useMaskConfig(props);
  const [showTemplateSelector, setShowTemplateSelector] = useState(true);

  if (mask.isCreateMode && showTemplateSelector) {
    return (
      <CheckListOverlayControllerTemplateSelector
        {...props}
        createNewCheckList={() => setShowTemplateSelector(false)}
      />
    );
  }

  return <CheckListOverlayControllerForm {...props} />;
};

const CheckListOverlayControllerTemplateSelector = ({
  createNewCheckList,
  ...rest
}: { createNewCheckList: () => void } & CheckListOverlayControllerProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mask = useMaskConfig(rest);

  const createChecklistFromTemplate = useCreateChecklistFromTemplate();
  const handleTemplateSelection = async (id: number) => {
    const template = mask?.template;
    const variables: CreateChecklistFromTemplateProps = {
      id,
    };

    let navigatePrefix = '';

    const templateEntity = template?.entity;
    const templateIds = template?.ids ?? [];
    const templateId = Number(templateIds[0]);

    if (templateEntity === 'customer' && templateId) {
      variables.customerId = templateId;
      variables.projectId = 0;
      navigatePrefix = `/${Entities.customer}s/${templateId}/details/${Entities.checkList}/`;
    } else if (templateEntity === 'project' && templateId) {
      variables.projectId = templateId;
      variables.customerId = 0;
      navigatePrefix = `/${Entities.project}s/${templateId}/details/${Entities.checkList}/`;
    } else {
      navigatePrefix = `/more/entity/${Entities.checkList}/details/`;
    }

    const checklist = await createChecklistFromTemplate(variables);

    navigate(`${navigatePrefix}${checklist.id}`, {
      replace: true,
    });
  };

  return (
    <MaskContextProvider value={mask}>
      <MaskOverlayHeader title={t('COMMON.CHECKLIST')} />
      <div className={styles.container}>
        <Button
          className={styles.createBlankCheckList}
          onClick={createNewCheckList}
          startIcon={<Add />}
        >
          {t('COMMON.CREATE_NEW_CHECKLIST')}
        </Button>

        <div className={styles.checkListPickerWrapper}>
          <CheckListPicker
            multiple={false}
            clearable={false}
            value={null}
            onChange={(value: CheckList) => {
              handleTemplateSelection(value.id);
            }}
            overrides={{
              data: { id: null, title: null },
              completeDataResponse: false,
              displayProperty: 'title',
            }}
            prefilter={[{ isSampleTemplate: { $eq: true } }]}
            filterBy="title"
            sortBy="title"
            layout="advanced"
            placeholder={t('COMMON.SEARCH_IN_TEMPLATES')}
          />
        </div>
      </div>
    </MaskContextProvider>
  );
};

const CheckListOverlayControllerForm = (
  props: CheckListOverlayControllerProps
) => {
  const mask = useMaskConfig(props);
  const request = useCheckListMaskOverlayRequest(mask.id);
  const newEntityData = useCheckListMaskOverlayNewEntityData(mask);
  const overlay = useMaskOverlay<CheckListMaskFormValue>({
    ...props,
    request,
    newEntityData,
    mask,
    getSubTitle: (x) => x.title,
  });

  useCheckListFormUpdate({ form: overlay.form });

  const onSubmit = useCheckListSubmitHandler({
    isCreateMode: mask.isCreateMode,
    dirtyFields: overlay.form.formState.dirtyFields,
    onAfterSave: props.onAfterSave,
    defaultValues: overlay.data,
  });

  return (
    <OverlayController<CheckListMaskFormValue>
      {...overlay}
      onSubmit={async (data) => {
        onSubmit(data);
      }}
      tabs={<CheckListOverlayControllerTabs />}
      openTab={mask.params?.tab ?? ControllerTabs.General}
    >
      <CheckListOverlayControllerContent />
    </OverlayController>
  );
};
